import React, { useMemo } from 'react';
import { InstantSearch } from 'react-instantsearch';
import { searchClient as algoliaClient } from '@/lib/search/instantsearch/algoliaAdapter';
import { sequencesRouting } from '@/lib/search/instantsearch/routing';

interface InstantSearchProviderProps {
  indexName: 'sequences' | 'sequences_v2' | 'displays';
  children: React.ReactNode;
  routing?: boolean;
}

/**
 * InstantSearch provider using Algolia
 */
export const InstantSearchProvider: React.FC<InstantSearchProviderProps> = ({
  indexName,
  children,
  routing = true,
}) => {
  // Resolve index name for Algolia
  const resolvedIndexName = useMemo(() => {
    // Map legacy index names to Algolia indices
    return indexName === 'sequences_v2' ? 'sequences' : indexName;
  }, [indexName]);

  // Determine routing configuration based on index and routing prop
  const routingConfig = useMemo(() => {
    // If routing is explicitly disabled, return false
    if (routing === false) {
      return false;
    }

    // Use custom routing for sequences index to support clean URL parameters (?q=)
    if (resolvedIndexName === 'sequences') {
      return sequencesRouting;
    }

    // Use default routing for other indices
    return true;
  }, [routing, resolvedIndexName]);

  if (!algoliaClient) {
    console.error('❌ Algolia client not available. Search will not work.');
    return <div>Search unavailable - missing Algolia credentials</div>;
  }

  return (
    <InstantSearch
      searchClient={algoliaClient}
      indexName={resolvedIndexName}
      routing={routingConfig}
    >
      {children}
    </InstantSearch>
  );
};